import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCalendarDays,
  faDoorOpen,
} from "@fortawesome/free-solid-svg-icons";
import "./BuildingRoomTable.css";

const normalizeDate = (dateValue) => {
  if (!dateValue || typeof dateValue !== "string") {
    return "";
  }

  const value = dateValue.trim();

  const germanDateMatch = value.match(
    /^(\d{2})\.(\d{2})\.(\d{4})$/
  );

  if (germanDateMatch) {
    const [, day, month, year] = germanDateMatch;

    return `${year}-${month}-${day}`;
  }

  const isoDateMatch = value.match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;

    return `${year}-${month}-${day}`;
  }

  return "";
};

const formatDate = (dateValue) => {
  const normalizedDate = normalizeDate(dateValue);

  if (!normalizedDate) {
    return "";
  }

  const [year, month, day] = normalizedDate.split("-");

  return `${day}.${month}.${year}`;
};

const formatCompactPeriod = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  if (!start || !end) {
    return "–";
  }

  const [startYear, startMonth, startDay] =
    start.split("-");

  const [endYear, endMonth, endDay] =
    end.split("-");

  if (start === end) {
    return `${startDay}.${startMonth}.${startYear}`;
  }

  if (
    startYear === endYear &&
    startMonth === endMonth
  ) {
    return (
      `${startDay}.${startMonth}. – ` +
      `${endDay}.${endMonth}.${endYear}`
    );
  }

  if (startYear === endYear) {
    return (
      `${startDay}.${startMonth}. – ` +
      `${endDay}.${endMonth}.${endYear}`
    );
  }

  return (
    `${startDay}.${startMonth}.${startYear} – ` +
    `${endDay}.${endMonth}.${endYear}`
  );
};

const formatPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "–";
  }

  if (startDate === endDate) {
    return formatDate(startDate);
  }

  return (
    `${formatDate(startDate)} – ` +
    `${formatDate(endDate)}`
  );
};

const hasSameLocation = (firstItem, secondItem) => {
  if (!firstItem || !secondItem) {
    return false;
  }

  return (
    String(firstItem.locationId ?? "") ===
      String(secondItem.locationId ?? "") &&
    String(firstItem.locationName ?? "") ===
      String(secondItem.locationName ?? "") &&
    String(firstItem.roomShortName ?? "") ===
      String(secondItem.roomShortName ?? "") &&
    String(firstItem.roomName ?? "") ===
      String(secondItem.roomName ?? "")
  );
};

/**
 * Gruppiert ausschließlich nach Änderungen der Raumdaten.
 *
 * Kalenderwochen, Wochenenden oder fehlende Tage erzeugen
 * keine neue Gruppe.
 */
const createLocationGroups = (items = []) => {
  const sortedItems = [...items]
    .filter((item) => normalizeDate(item?.date))
    .sort((a, b) =>
      normalizeDate(a.date).localeCompare(
        normalizeDate(b.date)
      )
    );

  const groups = [];

  for (const item of sortedItems) {
    const itemDate = normalizeDate(item.date);
    const lastGroup = groups.at(-1);
    const previousItem = lastGroup?.items.at(-1);

    const belongsToLastGroup =
      Boolean(lastGroup) &&
      hasSameLocation(previousItem, item);

    if (belongsToLastGroup) {
      lastGroup.endDate = itemDate;
      lastGroup.items.push(item);

      continue;
    }

    groups.push({
      startDate: itemDate,
      endDate: itemDate,
      locationId: item.locationId ?? "",
      locationName: item.locationName ?? "",
      roomShortName: item.roomShortName ?? "",
      roomName: item.roomName ?? "",
      items: [item],
    });
  }

  return groups;
};

const createRowKey = (group, index) =>
  [
    group.startDate,
    group.endDate,
    group.locationId,
    group.locationName,
    group.roomShortName,
    group.roomName,
    index,
  ].join("|");

const CompactView = ({
  groups,
  invalidDateRange,
  centerCompact,
}) => {
  if (invalidDateRange) {
    return (
      <div
        className={[
          "compact-message",
          "error-message",
          centerCompact
            ? "compact-message--center"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Das Von-Datum darf nicht nach dem Bis-Datum liegen.
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div
        className={[
          "compact-message",
          "empty-message",
          centerCompact
            ? "compact-message--center"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
            <FontAwesomeIcon
              icon={faDoorOpen}
              aria-hidden="true"
            /> Die Raumangabe folgt zu einem späteren Zeitpunkt.
      </div>
    );
  }

  const showPeriod = groups.length > 1;

  return (
    <div
      className={[
        "compact-location-list",
        centerCompact
          ? "compact-location-list--center"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {groups.map((group, index) => (
        <div
          key={createRowKey(group, index)}
          className={[
            "compact-location-row",
            centerCompact
              ? "compact-location-row--center"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {showPeriod && (
            <div>
            <span className="compact-location-item compact-location-period">
              <FontAwesomeIcon
                icon={faCalendarDays}
                aria-hidden="true"
              />

              <strong>
                {formatCompactPeriod(
                  group.startDate,
                  group.endDate
                )}
              </strong>
            </span>
            <br />
            </div>
          )}
          
          <span className="compact-location-item" style={{marginRight:'0.5em'}}>

            <span>
              {group.locationName || "–"}
            </span>
          </span>

          <span className="compact-location-item"
          style={{
              background:group.roomShortName?'#006472':'none',
              fontStyle:group.roomShortName?'normal':'italic',
              padding:'0em 0.5em',
              borderRadius:'0.5em'
          }}
          >
            <FontAwesomeIcon
            style={{
              color:group.roomShortName?'white':'#333'}}
              icon={faDoorOpen}
              aria-hidden="true"
            />

            <span style={{
              color:group.roomShortName?'white':'#333',fontWeight:group.roomShortName?'bold':'normal'}}>
              {group.roomShortName || "Raumangabe folgt zu einem späteren Zeitpunkt."}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

const DefaultView = ({
  von,
  bis,
  groups,
  invalidDateRange,
}) => {
  const showPeriod = groups.length > 1;
  const columnCount = showPeriod ? 5 : 4;

  return (
    <>
      <div className="table-header">
        <h2>Gebäude- und Raumübersicht</h2>

        {(von || bis) && (
          <span className="table-total-range">
            {von ? formatDate(von) : "Offen"}
            {" – "}
            {bis ? formatDate(bis) : "Offen"}
          </span>
        )}
      </div>

      <div className="table-wrapper">
        <table className="building-table">
          <thead>
            <tr>
              {showPeriod && (
                <th>Tag / Zeitraum</th>
              )}

              <th>Gebäude-Nr.</th>
              <th>Gebäudename</th>
              <th>Raum-Nr.</th>
              <th>Raumname</th>
            </tr>
          </thead>

          <tbody>
            {invalidDateRange ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="empty-message"
                >
                  Das Von-Datum darf nicht nach dem
                  Bis-Datum liegen.
                </td>
              </tr>
            ) : groups.length > 0 ? (
              groups.map((group, index) => (
                <tr key={createRowKey(group, index)}>
                  {showPeriod && (
                    <td>
                      <strong>
                        {formatPeriod(
                          group.startDate,
                          group.endDate
                        )}
                      </strong>
                    </td>
                  )}

                  <td>
                    {group.locationId || "–"}
                  </td>

                  <td>
                    {group.locationName || "–"}
                  </td>

                  <td>
                    {group.roomShortName || "–"}
                  </td>

                  <td>
                    {group.roomName || "–"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columnCount}
                  className="empty-message"
                >
                  Für den Zeitraum sind keine
                  Raumangaben vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default function BuildingRoomTable({
  von,
  bis,
  items = [],
  compactView = false,
  centerCompact = false,
}) {
  const invalidDateRange = useMemo(() => {
    const fromDate = normalizeDate(von);
    const toDate = normalizeDate(bis);

    return Boolean(
      fromDate &&
        toDate &&
        fromDate > toDate
    );
  }, [von, bis]);

  const groups = useMemo(() => {
    if (invalidDateRange) {
      return [];
    }

    const fromDate = normalizeDate(von);
    const toDate = normalizeDate(bis);

    const filteredItems = items.filter((item) => {
      const itemDate = normalizeDate(item?.date);

      if (!itemDate) {
        return false;
      }

      return (
        (!fromDate || itemDate >= fromDate) &&
        (!toDate || itemDate <= toDate)
      );
    });

    return createLocationGroups(filteredItems);
  }, [von, bis, items, invalidDateRange]);

  return (
    <div
      className={[
        "table-container",
        compactView
          ? "table-container--compact"
          : "",
        compactView && centerCompact
          ? "table-container--compact-center"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {compactView ? (
        <CompactView
          groups={groups}
          invalidDateRange={invalidDateRange}
          centerCompact={centerCompact}
        />
      ) : (
        <DefaultView
          von={von}
          bis={bis}
          groups={groups}
          invalidDateRange={invalidDateRange}
        />
      )}
    </div>
  );
}